import { User } from '@grammarly/http-clients-auth'
import { Money } from '@grammarly/http-clients-subscription'
import { Treatment } from '@grammarly/experimentation-js-sdk'

import { TypePage } from '../../types'
import { getClientCapabilities } from '../../utils/client-capabilities'
import { isEmpty } from '../../utils/object'
import { DEFAULT_LOCALE } from '@lib/constants'
import { Locale } from '@lib/utils/locale'

export enum ClientType {
  Browser = 'WebClient',
  Desktop = 'DesktopClient',
  Unknown = 'Unknown',
}

// These values correspond to deviceType returned from Facade
export enum DeviceType {
  Computer = 'computer',
  Desktop = 'desktop',
  Mobile = 'mobile',
  Tablet = 'tablet',
  Unknown = 'unknown',
}

export enum BrowserType {
  Chrome = 'chrome',
  Firefox = 'firefox',
  Safari = 'safari',
  IE = 'internet explorer',
  Edge = 'microsoft edge',
  Unknown = 'unknown',
}

export enum DesktopOsType {
  Mac = 'mac',
  Windows = 'windows',
}

export enum MobileOsType {
  iOS = 'ios',
  Android = 'android',
}

export type DeviceOsType = DesktopOsType | MobileOsType

export interface Localization {
  locale: Locale
  defaultLocale: Locale
  supportedLocales: Locale[]
  isDefaultLocale?: boolean
}

export interface ClientCapabilities {
  clientType: ClientType
  version: string | 'unknown' // full version string or "unknown"
  majorVersion: number // -1 in case of unknown version
  deviceType: DeviceType
  os: string
  osGroup: string
  browser: string | 'Unknown' | null | undefined
  // GSF-1842 -- Mac OS Version string. Temporary field to support Llama for Mac experiment. Do not use!
  osVersion?: string
}

export interface DiscountData {
  name: string
  offerType: string
  value: string
}

export interface ProPlan {
  id: number
  priceMoney: Money
  periodMonths: number
}

export interface ProfileExperience {
  experienceId: string
  variantIndex: number
  audiences: string[]
}

export interface FacadeData {
  requestId: string
  pageSlug?: string
  pageEntry?: TypePage
  localization?: Localization
  user?: User
  clientCapabilities?: ClientCapabilities
  treatments: Treatment[]
  serverSideAssignTreatments: Treatment[]
  discount?: DiscountData
  passthroughCookies: Record<string, string | undefined>
  proPlans: ProPlan[]
  profileExperiences: ProfileExperience[]
  geoLocation?: {
    cityName?: string
    subdivisionName?: string
    postalCode?: string
    countryCode?: string
    countryName?: string
  }
  specialOffers?: Record<string, boolean>
}

export type UserAgentString = string

export const getDefaultFacadeData = (userAgent?: UserAgentString): FacadeData => {
  return {
    requestId: 'unknown',
    pageSlug: 'unknown',
    pageEntry: {} as TypePage,
    localization: {
      locale: DEFAULT_LOCALE,
      defaultLocale: DEFAULT_LOCALE,
      supportedLocales: [DEFAULT_LOCALE],
    },
    treatments: [],
    serverSideAssignTreatments: [],
    clientCapabilities: getClientCapabilities(userAgent),
    passthroughCookies: {},
    proPlans: [],
    profileExperiences: [],
    specialOffers: {},
  }
}

export interface FacadeService {
  get: () => FacadeData
  pick: (key: keyof FacadeData) => FacadeData[keyof FacadeData]
}

export class FacadeServiceImpl implements FacadeService {
  private readonly cachedData: FacadeData
  constructor(cachedData?: FacadeData, userAgent?: UserAgentString) {
    this.cachedData =
      cachedData && !isEmpty(cachedData)
        ? cachedData
        : getDefaultFacadeData(IS_CLIENT_SIDE ? window.navigator.userAgent : userAgent)
  }

  get(): FacadeData {
    return (IS_CLIENT_SIDE && window.Grammarly) || this.cachedData
  }

  pick(key: keyof FacadeData): FacadeData[keyof FacadeData] {
    return this.get()[key] || this.cachedData[key]
  }
}
